import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ShieldCheck, BadgeCheck, X, ShieldAlert, FileCheck, Clock } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Cleaning", "Beauty", "Childcare", "Chef", "Decor", "Grocery", "Auto", "Transport", "Handyman", "Laundry", "Moving"];
const CITIES = ["Kingston", "Portmore", "Spanish Town", "Montego Bay", "Ocho Rios", "Mandeville"];

export default function ProviderApplyPage() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();

    const [bio, setBio] = useState("");
    const [skills, setSkills] = useState("");
    const [serviceCategories, setServiceCategories] = useState([]);
    const [hourlyRate, setHourlyRate] = useState(2500);
    const [locations, setLocations] = useState(["Kingston"]);
    const [profilePic, setProfilePic] = useState("");
    const [idDoc, setIdDoc] = useState("");
    const [license, setLicense] = useState("");
    const [policeRecord, setPoliceRecord] = useState("");
    const [trn, setTrn] = useState("");
    const [proofOfAddress, setProofOfAddress] = useState("");
    const [yearsExp, setYearsExp] = useState(1);
    const [certificates, setCertificates] = useState([]);
    const [portfolio, setPortfolio] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [profile, setProfile] = useState(null);

    const [authChecked, setAuthChecked] = useState(false);

    // CRITICAL FIX (CEO-flagged production bug): unauthenticated visitors
    // who tried to use the file-uploaders on /become-a-pro were getting
    // "Not authenticated" 401s the moment they touched the Gov-ID upload.
    // Redirect to the signup flow up front instead of letting them touch
    // any auth-only endpoint. After they register they're auto-bounced back
    // here to fill in the rest.
    useEffect(() => {
        if (user === null) {
            // user resolved to null (definitively unauthenticated, not just
            // loading) — kick them to register, then back to /become-a-pro.
            navigate("/auth?role=provider&mode=register&next=/become-a-pro", {
                replace: true,
            });
            return;
        }
        if (user) setAuthChecked(true);
    }, [user, navigate]);

    useEffect(() => {
        if (!user) return;
        api.get("/providers/me")
            .then(({ data }) => {
                setProfile(data);
                setBio(data.bio || "");
                setSkills((data.skills || []).join(", "));
                setServiceCategories(data.service_categories || []);
                setHourlyRate(data.hourly_rate || 2500);
                setLocations(data.locations || ["Kingston"]);
                setProfilePic(data.profile_picture_url || "");
                setIdDoc(data.id_document_url || "");
                setLicense(data.license_url || "");
                setPoliceRecord(data.police_record_url || "");
                setTrn(data.trn_url || "");
                setProofOfAddress(data.proof_of_address_url || "");
                setYearsExp(data.years_experience || 1);
                setCertificates(data.certificates || []);
                setPortfolio(data.portfolio || []);
            })
            .catch(() => {});
    }, [user]);

    const toggle = (arr, setArr, val) => {
        if (arr.includes(val)) setArr(arr.filter((v) => v !== val));
        else setArr([...arr, val]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate("/auth?role=provider&mode=register");
            return;
        }
        // GRADUATED VETTING — only the 4 quick-start docs are mandatory at signup.
        // Police record + trade licence have a 90-day grace period after which
        // the account auto-suspends until uploaded.
        if (!profilePic || !idDoc) {
            toast.error("Profile picture and Government ID are required to start.");
            return;
        }
        if (!trn) {
            toast.error("Your TRN is required for tax-compliant payouts.");
            return;
        }
        if (!proofOfAddress) {
            toast.error("Proof of address (utility bill / bank statement) is required.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/providers/apply", {
                bio,
                skills: skills.split(",").map((s) => s.trim()).filter(Boolean),
                service_categories: serviceCategories,
                hourly_rate: parseFloat(hourlyRate),
                locations,
                profile_picture_url: profilePic,
                id_document_url: idDoc,
                license_url: license || null,
                police_record_url: policeRecord || null,
                trn_url: trn,
                proof_of_address_url: proofOfAddress,
                certificates,
                portfolio,
                years_experience: parseInt(yearsExp) || 0,
            });
            setProfile(data);
            await refresh();
            const level = data.verification_level;
            if (level === "royal") {
                toast.success("All documents in — you're now Fully Royal Verified.");
            } else if (level === "provisional") {
                toast.success("You're Provisionally Verified — start accepting bookings now. Upload your Police Certificate + Trade Licence within 90 days to keep your status.");
            } else {
                toast.success("Application updated.");
            }
            setTimeout(() => navigate("/provider"), 1400);
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setSubmitting(false);
        }
    };

    const statusBadge = profile?.application_status;

    // Don't render the form (with auth-only file uploaders) until we've
    // confirmed an authenticated user — the useEffect above redirects
    // unauthenticated visitors to register first.
    if (!authChecked || !user) {
        return (
            <div className="min-h-screen bg-background grid place-items-center" data-testid="provider-apply-redirecting">
                <div className="text-center">
                    <div className="overline mb-3">For Professionals</div>
                    <h1 className="font-serif text-2xl">Taking you to sign-up…</h1>
                    <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
                        We're routing you through a 30-second registration first — file uploads need a Royal account to attach to.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="provider-apply-page">
            <Header />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <div className="overline mb-4">For Professionals</div>
                        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
                            Earn on your<br />
                            <span className="italic gold-grad">own schedule.</span>
                        </h1>
                        <p className="mt-8 text-base text-muted-foreground leading-relaxed">
                            Join SMART — Jamaica's safest on-demand network. We bring you premium clients, you bring the craft. <strong className="gold-text">No application fees ever.</strong>
                        </p>

                        <div className="mt-10 p-6 bg-card border border-primary/30 rounded-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <ShieldAlert size={14} className="gold-text" strokeWidth={1.5} />
                                <div className="overline">Safety & Verification</div>
                            </div>
                            <ul className="space-y-2 text-xs text-muted-foreground">
                                <li>• Government-issued ID required (admin reviewed)</li>
                                <li>• Profile picture so clients see who's coming</li>
                                <li>• On-job hourly safety ping; missed pings escalate to admin & police</li>
                                <li>• Cash payments are NOT permitted — all settled in-app</li>
                                <li>• SMART will never dispatch you to admin-flagged unsafe zones</li>
                            </ul>
                        </div>

                        <div
                            className="mt-6 p-6 bg-gradient-to-br from-primary/10 via-card to-card border border-primary/40 rounded-sm relative overflow-hidden"
                            data-testid="vetting-checklist"
                        >
                            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-primary/10 blur-2xl" />
                            <div className="flex items-center gap-2 mb-2 relative">
                                <FileCheck size={14} className="gold-text" strokeWidth={1.5} />
                                <div className="overline">Graduated Vetting</div>
                            </div>
                            <p className="text-xs text-muted-foreground mb-5 relative">
                                Start in under 2 minutes with the <span className="gold-text">4 quick-start docs</span>. You'll be <strong className="text-amber-300">Provisionally Verified</strong> instantly and can take bookings while you finish the rest within 90 days.
                            </p>
                            <ol className="space-y-4 relative">
                                {[
                                    {
                                        title: "Valid Government ID",
                                        body: "Passport, National ID, or Driver's License. Clear photo of front (and back if double-sided).",
                                        bucket: "quick",
                                    },
                                    {
                                        title: "Profile Picture",
                                        body: "A clear, friendly headshot — clients see this when they book you.",
                                        bucket: "quick",
                                    },
                                    {
                                        title: "Taxpayer Registration Number (TRN)",
                                        body: "TRN card or letter — required for tax-compliant payouts.",
                                        bucket: "quick",
                                    },
                                    {
                                        title: "Proof of Address",
                                        body: "Utility bill or bank statement, not older than 3 months. Must show your name + current address.",
                                        bucket: "quick",
                                    },
                                    {
                                        title: "Recent Police Certificate",
                                        body: "Issued within the last 6 months. JCF Records office or e-PRC online. Upload within 90 days.",
                                        bucket: "hard",
                                    },
                                    {
                                        title: "Trade Licence or Certification",
                                        body: "If applicable to your trade (electrical, plumbing, beauty, food handler). Upload within 90 days.",
                                        bucket: "hard",
                                    },
                                ].map((item, i) => (
                                    <li key={`checklist-${item.title}`} className="flex gap-3" data-testid={`checklist-item-${i}`}>
                                        <span className={`shrink-0 mt-0.5 w-6 h-6 grid place-items-center rounded-sm text-[10px] font-semibold ${
                                            item.bucket === "quick"
                                                ? "bg-primary text-primary-foreground"
                                                : "bg-amber-900/40 text-amber-300 border border-amber-700/40"
                                        }`}>
                                            {i + 1}
                                        </span>
                                        <div>
                                            <div className="text-sm text-foreground font-medium leading-snug">
                                                {item.title}
                                                <span className={`ml-2 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] rounded-sm ${
                                                    item.bucket === "quick"
                                                        ? "bg-primary/20 gold-text"
                                                        : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                                                }`}>
                                                    {item.bucket === "quick" ? "Day 0" : "90-day grace"}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                                                {item.body}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>

                            <div className="mt-6 pt-4 border-t border-white/10 flex gap-3 items-start" data-testid="checklist-pending-notice">
                                <Clock size={14} className="gold-text shrink-0 mt-0.5" strokeWidth={1.5} />
                                <p className="text-[11px] text-muted-foreground leading-relaxed">
                                    Submit the 4 Day-0 docs → instant <strong className="text-amber-300">Provisionally Verified</strong> badge. Add the 2 hard docs anytime in your first 90 days → upgraded to <strong className="gold-text">Fully Royal Verified</strong>. Past day 90 without hard docs = account paused from new bookings.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 p-6 bg-card border border-white/10 rounded-sm">
                            <div className="overline mb-3">Tier System</div>
                            <ul className="space-y-2 text-xs text-muted-foreground">
                                <li><span className="gold-text font-semibold">Expert</span> — extensive license, certifications, portfolio. Premium pricing.</li>
                                <li><span className="text-amber-300 font-semibold">Medium</span> — proven experience and references.</li>
                                <li><span className="text-zinc-300 font-semibold">Starter</span> — newer pros, fair-priced. Everyone deserves a shot.</li>
                            </ul>
                            <div className="mt-3 text-xs text-muted-foreground">Tier is set by admin after reviewing your documents and portfolio.</div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            onSubmit={handleSubmit}
                            className="bg-card border border-white/10 p-8 lg:p-10 rounded-sm space-y-6"
                            data-testid="provider-apply-form"
                        >
                            <div>
                                <div className="overline mb-2">Application</div>
                                <h2 className="font-serif text-3xl">Tell us about your craft.</h2>
                            </div>

                            {statusBadge && (
                                <ApplicationStatusBanner status={statusBadge} tier={profile?.tier} adminNotes={profile?.admin_notes} />
                            )}

                            <Field label="Bio">
                                <Textarea
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    rows={4}
                                    placeholder="3 years experience as a senior nail tech in Kingston. Specialised in gel & acrylic..."
                                    className="bg-input border-white/10 rounded-sm"
                                    required
                                    data-testid="provider-bio-input"
                                />
                            </Field>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Years of experience">
                                    <Input
                                        type="number"
                                        min="0"
                                        value={yearsExp}
                                        onChange={(e) => setYearsExp(e.target.value)}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        data-testid="provider-years-input"
                                    />
                                </Field>
                                <Field label="Hourly rate (JMD)">
                                    <Input
                                        type="number"
                                        value={hourlyRate}
                                        onChange={(e) => setHourlyRate(e.target.value)}
                                        className="bg-input border-white/10 h-11 rounded-sm"
                                        required
                                        data-testid="provider-rate-input"
                                    />
                                </Field>
                            </div>

                            <Field label="Skills (comma separated)">
                                <Input
                                    value={skills}
                                    onChange={(e) => setSkills(e.target.value)}
                                    placeholder="Gel manicure, Pedicure, Nail art"
                                    className="bg-input border-white/10 h-11 rounded-sm"
                                    required
                                    data-testid="provider-skills-input"
                                />
                            </Field>

                            <Field label="Service Categories">
                                <div className="flex flex-wrap gap-3 pt-2" data-testid="provider-categories">
                                    {CATEGORIES.map((c) => (
                                        <label
                                            key={c}
                                            className={`flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-sm text-xs uppercase tracking-[0.2em] ${
                                                serviceCategories.includes(c)
                                                    ? "border-primary bg-primary/10 text-primary"
                                                    : "border-white/10 text-muted-foreground hover:text-foreground"
                                            }`}
                                            data-testid={`provider-category-${c}`}
                                        >
                                            <Checkbox
                                                checked={serviceCategories.includes(c)}
                                                onCheckedChange={() => toggle(serviceCategories, setServiceCategories, c)}
                                                className="border-white/20"
                                            />
                                            {c}
                                        </label>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Service Locations">
                                <div className="flex flex-wrap gap-3 pt-2" data-testid="provider-locations">
                                    {CITIES.map((c) => (
                                        <label
                                            key={c}
                                            className={`flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-sm text-xs uppercase tracking-[0.2em] ${
                                                locations.includes(c)
                                                    ? "border-primary bg-primary/10 text-primary"
                                                    : "border-white/10 text-muted-foreground hover:text-foreground"
                                            }`}
                                            data-testid={`provider-location-${c}`}
                                        >
                                            <Checkbox
                                                checked={locations.includes(c)}
                                                onCheckedChange={() => toggle(locations, setLocations, c)}
                                                className="border-white/20"
                                            />
                                            {c}
                                        </label>
                                    ))}
                                </div>
                            </Field>

                            <div className="hairline" />
                            <div className="overline">Verification documents (admin-reviewed)</div>
                            <div className="text-[11px] text-muted-foreground -mt-3">
                                Files are stored encrypted and only visible to SMART admins for verification.
                            </div>

                            <FileUpload
                                purpose="profile_pic"
                                value={profilePic}
                                onUploaded={(f) => setProfilePic(f.id)}
                                onCleared={() => setProfilePic("")}
                                label="Profile picture *"
                                accept="image/*"
                                testid="provider-profile-pic"
                            />
                            <FileUpload
                                purpose="id_document"
                                value={idDoc}
                                onUploaded={(f) => setIdDoc(f.id)}
                                onCleared={() => setIdDoc("")}
                                label="Government ID * (passport, national ID, driver's licence)"
                                testid="provider-id-doc"
                            />
                            <FileUpload
                                purpose="police_record"
                                value={policeRecord}
                                onUploaded={(f) => setPoliceRecord(f.id)}
                                onCleared={() => setPoliceRecord("")}
                                label="Police Certificate (90-day grace — JCF Records or e-PRC, within last 6 months)"
                                testid="provider-police-record"
                            />
                            <FileUpload
                                purpose="trn"
                                value={trn}
                                onUploaded={(f) => setTrn(f.id)}
                                onCleared={() => setTrn("")}
                                label="TRN document * (Taxpayer Registration card or letter)"
                                testid="provider-trn"
                            />
                            <FileUpload
                                purpose="proof_of_address"
                                value={proofOfAddress}
                                onUploaded={(f) => setProofOfAddress(f.id)}
                                onCleared={() => setProofOfAddress("")}
                                label="Proof of Address * (utility bill or bank statement, last 3 months)"
                                testid="provider-proof-of-address"
                            />
                            <FileUpload
                                purpose="license"
                                value={license}
                                onUploaded={(f) => setLicense(f.id)}
                                onCleared={() => setLicense("")}
                                label="Trade licence / Certification (90-day grace — for Royal Verified upgrade)"
                                testid="provider-license"
                            />

                            <Field label="Certifications (upload one at a time)">
                                <FileUpload
                                    purpose="certificate"
                                    value=""
                                    onUploaded={(f) => setCertificates([...certificates, f.id])}
                                    label=""
                                    testid="provider-cert"
                                />
                                {certificates.length > 0 && (
                                    <ul className="mt-3 space-y-1" data-testid="provider-cert-list">
                                        {certificates.map((c, i) => (
                                            <li key={`cert-${c}`} className="flex items-center justify-between text-xs text-muted-foreground bg-input px-3 py-2 rounded-sm">
                                                <span className="truncate flex-1">Certificate #{i + 1} — {c.length > 40 ? c.slice(0, 8) + "…" : c}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setCertificates(certificates.filter((_, j) => j !== i))}
                                                    className="ml-2 hover:text-red-400"
                                                    aria-label="remove"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Field>

                            <Field label="Portfolio (proof-of-work — upload one at a time)">
                                <FileUpload
                                    purpose="portfolio"
                                    value=""
                                    onUploaded={(f) => setPortfolio([...portfolio, f.id])}
                                    label=""
                                    accept="image/*"
                                    testid="provider-portfolio"
                                />
                                {portfolio.length > 0 && (
                                    <ul className="mt-3 space-y-1" data-testid="provider-portfolio-list">
                                        {portfolio.map((p, i) => (
                                            <li key={`portfolio-${p}`} className="flex items-center justify-between text-xs text-muted-foreground bg-input px-3 py-2 rounded-sm">
                                                <span className="truncate flex-1">Portfolio item #{i + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setPortfolio(portfolio.filter((_, j) => j !== i))}
                                                    className="ml-2 hover:text-red-400"
                                                    aria-label="remove"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Field>

                            <Button
                                type="submit"
                                disabled={submitting || serviceCategories.length === 0 || locations.length === 0 || !profilePic || !idDoc}
                                className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12"
                                data-testid="provider-submit-btn"
                            >
                                {submitting ? "Submitting..." : profile ? "Update Profile" : "Submit Application"}{" "}
                                <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                            </Button>
                            <p className="text-[11px] text-muted-foreground">
                                By submitting you agree to background verification, the safety pinging policy, and to settle all jobs through the SMART app (no off-platform cash exchange).
                            </p>
                        </form>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block">{label}</Label>
            {children}
        </div>
    );
}

function ApplicationStatusBanner({ status, tier, adminNotes }) {
    const config = {
        pending: { label: "Pending Review", icon: ShieldAlert, color: "bg-amber-900/30 text-amber-300 border-amber-700/40" },
        under_review: { label: "Under Review", icon: ShieldCheck, color: "bg-blue-900/30 text-blue-300 border-blue-700/40" },
        approved: { label: "Approved", icon: BadgeCheck, color: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40" },
        rejected: { label: "Rejected", icon: X, color: "bg-red-900/30 text-red-300 border-red-700/40" },
    }[status] || { label: status, icon: ShieldAlert, color: "bg-card text-muted-foreground border-white/10" };
    const Icon = config.icon;
    return (
        <div className={`flex items-start gap-3 p-4 border rounded-sm ${config.color}`} data-testid={`apply-status-${status}`}>
            <Icon size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
                <div className="text-sm font-semibold uppercase tracking-[0.2em]">{config.label}</div>
                {tier && status === "approved" && (
                    <div className="mt-1 text-xs">Assigned tier: <span className="gold-text uppercase tracking-[0.2em]">{tier}</span></div>
                )}
                {adminNotes && <div className="mt-1 text-xs opacity-90">Admin note: {adminNotes}</div>}
            </div>
        </div>
    );
}
